import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCustomer } from "@/contexts/CustomerContext";
import { useAdmin } from "@/contexts/AdminContext";
import { TAXI_COMMANDER_LOGO } from "@/lib/brandAssets"; // v2.7.65+E173 · Dispatch System hero mark
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Shield, Car, User, ClipboardCheck, Mail, Building2, ArrowLeft } from "lucide-react";
import PaymentLogos from "@/components/PaymentLogos";
import GoogleSignIn from "@/components/GoogleSignIn";
import ClosedHoursDialog from "@/components/ClosedHoursDialog";
import MagicLinkButton from "@/components/MagicLinkButton";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import SmsConsent from "@/components/marketing/SmsConsent";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import useSiteConfig from "@/hooks/useSiteConfig";
import { getSubdomain, getDefaultLoginTab } from "@/lib/subdomain";

export default function LoginPage() {
  const { loginEmail, loginPin } = useAuth();
  const { login: loginCustomer, register: registerCustomer } = useCustomer();
  const nav = useNavigate();
  // v2.7.65+E69.1 · Honor ?tab=driver&sub=apply so the Google-Apply
  // callback lands the applicant directly on the pre-filled Apply form.
  const [initialTab, initialSub] = (() => {
    if (typeof window === "undefined") return [null, null];
    const q = new URLSearchParams(window.location.search);
    return [q.get("tab"), q.get("sub")];
  })();
  const [tab, setTab] = useState(() => initialTab || getDefaultLoginTab(getSubdomain()));
  const [driverSubtab, setDriverSubtab] = useState(() => (initialSub === "apply" ? "apply" : "email"));
  const [contact, setContact] = useState({ contact_phone: "(580) 219-4687", contact_email: "support@bullertaxi.com" });
  const [branding, setBranding] = useState({});
  const { cfg } = useSiteConfig();

  useEffect(() => { api.get("/dispatch/contact").then(r => setContact(r.data)).catch(()=>{}); }, []);

  // v2.8 · Riders belong on the dedicated Rider Portal. Whenever the
  // "Book My TAXICab" tab is active (apex default or an explicit click),
  // bounce straight to /ride/login so riders always land on the right portal.
  useEffect(() => {
    if (tab === "ride") nav("/ride/login", { replace: true });
  }, [tab, nav]);
  useEffect(() => { api.get("/branding").then(r => setBranding(r.data || {})).catch(()=>{}); }, []);

  // v2.6.9 batch CA · Admin-supplied backdrop per active tab. Driver tab
  // uses login_driver_bg_url, Dispatch tab uses login_dispatch_bg_url.
  // Falls back to the bundled /login-bg.jpg until an admin uploads one.
  const g = cfg?.global || {};
  const tabBg = tab === "dispatch" ? g.login_dispatch_bg_url : g.login_driver_bg_url;
  const loginBgUrl = tabBg || "/login-bg.jpg";

  return (
    <div
      className="min-h-screen relative flex items-center justify-center p-5 overflow-hidden"
      style={{
        backgroundImage: `url("${loginBgUrl}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundColor: "#0c0d10",
      }}
      data-testid="login-page-root"
    >
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.55) 100%)" }}/>

      {/* v2.8+E303 · Non-blocking "We Are Currently Closed" popup — fires only
          on the TAXIcab Driver tab when outside operating hours. Fully
          dismissible; never locks a driver out. */}
      <ClosedHoursDialog active={tab === "driver"} />

      {/* v2.6.9 batch BR · Back to Home — public marketing site */}
      <Link
        to="/"
        data-testid="driver-back-home"
        className="absolute top-4 left-4 z-20 inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 bg-zinc-950/70 backdrop-blur px-3 py-2 rounded-md border border-amber-400/30 uppercase tracking-wider font-bold"
      >
        <ArrowLeft className="h-3.5 w-3.5"/> Back to Home
      </Link>

      <div className="w-full max-w-2xl relative z-10">
        <div className="mb-6 sm:mb-8 text-center" data-testid="login-page-header">
          {/* v2.7.65+E173 · Prefer the CMS-configured logo when the admin
              has customized it; otherwise render the centralized
              TAXI_COMMANDER_LOGO ("BULLER TAXI SERVICES · DISPATCH SYSTEM"
              crest + gold-text lockup) so branding stays consistent
              from first touch. */}
          <img
            src={cfg?.global?.taxi_commander_logo_url || TAXI_COMMANDER_LOGO}
            alt="Buller Taxi Services · Dispatch System"
            className="mx-auto h-28 sm:h-36 object-contain select-none pointer-events-none drop-shadow-[0_4px_18px_rgba(250,204,21,0.35)]"
            data-testid="login-commander-logo"
          />
        </div>

        <Card className="bg-zinc-900/80 border-zinc-800 p-5 sm:p-6 backdrop-blur-xl glow-amber">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-3 bg-zinc-950 mb-5 h-auto" data-testid="login-tabs">
              <TabsTrigger value="ride"     data-testid="tab-ride"     className="text-[10px] sm:text-[11px] py-2"><User className="h-3 w-3 mr-1"/>Book My TAXICab</TabsTrigger>
              <TabsTrigger value="driver"   data-testid="tab-driver"   className="text-[10px] sm:text-[11px] py-2"><Car className="h-3 w-3 mr-1"/>TAXIcab Driver</TabsTrigger>
              <TabsTrigger value="dispatch" data-testid="tab-dispatch" className="text-[10px] sm:text-[11px] py-2"><Shield className="h-3 w-3 mr-1"/>TAXIcab Dispatch</TabsTrigger>
            </TabsList>

            <TabsContent value="ride"><RidePanel login={loginCustomer} register={registerCustomer} nav={nav}/></TabsContent>
            <TabsContent value="driver"><DriverPanel loginEmail={loginEmail} nav={nav} initialSubtab={driverSubtab}/></TabsContent>
            <TabsContent value="dispatch"><DispatchPanel nav={nav} destination="/admin"/></TabsContent>
            {/* v2.6.7 batch I · Portal tab hidden (the /portal page itself is
                also hidden via App.js feature flag). Code retained for the
                next phase when API keys are wired in. */}
          </Tabs>
        </Card>

        {/* v2.6.9 batch CI · Bottom footer metadata REMOVED per spec —
            Contact Us block, PaymentLogos, and legal-link bar are no longer
            rendered on the Driver / Dispatch login surface. Forgot Password
            (inside DriverPanel) and Back-to-Home (top-left) are preserved. */}
      </div>
    </div>
  );
}

function DispatchPanel({ nav, destination = "/admin", variant = "dispatch" }) {
  // v2.6.9 batch BD-2 · Supervisor PIN sub-tab removed from UI per user request
  // (2026-05-24). Backend /api/admin/login-pin still works for any future
  // re-enable. Forgot Password link remains as the self-serve recovery path.
  const { login: adminLogin, verify2fa } = useAdmin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);  // v2.7.65+E71
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  // v2.6.7 batch H · 2FA challenge state. When the server replies with
  // requires_2fa=true we stash the pending_token and show a second screen
  // that takes either a 6-digit TOTP or a 10-char backup code.
  const [twofa, setTwofa] = useState({ pendingToken: null, code: "" });
  const isPortal = variant === "portal";
  const ctaLabel = isPortal ? "ENTER PORTAL" : "ENTER TAXIcab DISPATCH";
  const Icon = isPortal ? Building2 : Shield;

  const handleLoginResp = (data) => {
    if (data?.requires_2fa) { setTwofa({ pendingToken: data.pending_token, code: "" }); return false; }
    nav(destination);
    return true;
  };

  const submitEmail = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const data = await adminLogin(email, password, remember); handleLoginResp(data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  const submit2fa = async () => {
    if (!twofa.pendingToken || !twofa.code) return;
    setBusy(true);
    try { await verify2fa(twofa.pendingToken, twofa.code.trim()); nav(destination); }
    catch (err) { toast.error(err?.response?.data?.detail || "Invalid 2FA code"); setTwofa(t => ({...t, code: ""})); }
    finally { setBusy(false); }
  };

  // Step 2 — 2FA challenge
  if (twofa.pendingToken) {
    return (
      <div className="space-y-4 text-center" data-testid={isPortal ? "portal-2fa" : "dispatch-2fa"}>
        <Icon className="h-8 w-8 mx-auto text-amber-400"/>
        <div>
          <div className="font-bold text-zinc-100">Two-factor authentication</div>
          <div className="text-xs text-zinc-500 mt-1">Enter the 6-digit code from your authenticator app, or paste a one-time backup code.</div>
        </div>
        <Input
          data-testid={isPortal ? "portal-2fa-code" : "dispatch-2fa-code"}
          autoFocus
          value={twofa.code}
          onChange={(e) => setTwofa(t => ({...t, code: e.target.value}))}
          onKeyDown={(e) => e.key === "Enter" && submit2fa()}
          placeholder="123456 or backup code"
          className="bg-zinc-950 border-zinc-700 h-12 text-center text-lg meter-display tracking-[0.4em]"
        />
        <Button
          data-testid={isPortal ? "portal-2fa-submit" : "dispatch-2fa-submit"}
          onClick={submit2fa}
          disabled={busy || !twofa.code.trim()}
          className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "VERIFY & CONTINUE"}
        </Button>
        <button
          type="button"
          onClick={() => setTwofa({ pendingToken: null, code: "" })}
          className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300"
          data-testid={isPortal ? "portal-2fa-cancel" : "dispatch-2fa-cancel"}
        >
          ← back to login
        </button>
      </div>
    );
  }

  return (
    <div data-testid={isPortal ? "portal-panel" : "dispatch-panel"}>
      {isPortal && (
        <div className="mb-4 rounded-md bg-amber-400/10 border border-amber-400/40 px-3 py-2 flex items-start gap-2" data-testid="portal-hint">
          <Building2 className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0"/>
          <div className="text-[11px] text-amber-200/90 leading-snug">
            Sign in with your <span className="font-bold">supervisor or admin</span> credentials to open the Company Portal.
          </div>
        </div>
      )}

      {/* v2.6.9 batch BD-2 · Supervisor PIN sub-tab hidden per user request.
          Backend /api/admin/login-pin endpoint is left intact for any future
          re-enable; only the UI surface is removed. Forgot Password link
          remains as the self-serve recovery path. */}
      {/* v2.7.65+E69 · Continue with Google (staff/admin portal, LINK-ONLY).
          Unknown emails are rejected — must be a pre-existing elevated
          staff account. Lands on /auth/admin-callback. */}
      <GoogleSignIn redirectPath="/auth/admin-callback" testid={isPortal ? "portal-google-signin-btn" : "dispatch-google-signin-btn"}/>
      <MagicLinkButton portal="admin" defaultEmail={email} testid={isPortal ? "portal-magic-link-btn" : "dispatch-magic-link-btn"}/>
      <form onSubmit={submitEmail} className="space-y-3 mt-3">
        <Input data-testid={isPortal ? "portal-email-input" : "dispatch-email-input"} type="text" placeholder="Username or email" value={email} onChange={e=>setEmail(e.target.value)} required className="bg-zinc-950 border-zinc-700 h-11"/>
        <Input data-testid={isPortal ? "portal-password-input" : "dispatch-password-input"} type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} required className="bg-zinc-950 border-zinc-700 h-11"/>
        <label className="flex items-center gap-2 cursor-pointer" data-testid={isPortal ? "portal-remember-me" : "dispatch-remember-me"}>
          <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="border-zinc-700 data-[state=checked]:bg-amber-400 data-[state=checked]:border-amber-400 data-[state=checked]:text-zinc-950"/>
          <span className="text-[11px] text-zinc-400">Remember me for 30 days</span>
        </label>
        <Button data-testid={isPortal ? "portal-login-btn" : "dispatch-login-btn"} type="submit" disabled={busy} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Icon className="h-4 w-4 mr-1"/>{ctaLabel}</>}
        </Button>
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => setShowForgot(true)} data-testid="dispatch-forgot-pw" className="text-[11px] text-zinc-400 hover:text-amber-400 underline-offset-2 hover:underline">
            Forgot password?
          </button>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Secured by 2FA</p>
        </div>
      </form>

      <ForgotPasswordDialog open={showForgot} onOpenChange={setShowForgot} defaultEmail={email}/>
    </div>
  );
}

function DriverPanel({ loginEmail, nav, initialSubtab = "email" }) {
  // v2.6.7 batch I · PIN login removed for drivers per security review. Email
  // + password is the only entry point; Apply tab still available for new drivers.
  // v2.6.9 batch BD · "Forgot password?" link added.
  // v2.7.65+E69.1 · initialSubtab lets the Google-Apply callback route land
  // the applicant directly on the "Apply" sub-tab.
  const [mode, setMode] = useState(initialSubtab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);  // v2.7.65+E71
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await loginEmail(email, password, remember); nav("/dashboard"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <Tabs value={mode} onValueChange={setMode}>
      <TabsList className="grid grid-cols-2 bg-zinc-950 mb-4 h-9">
        <TabsTrigger value="email" data-testid="drv-tab-email" className="text-xs">Email</TabsTrigger>
        <TabsTrigger value="apply" data-testid="drv-tab-apply" className="text-xs"><ClipboardCheck className="h-3 w-3 mr-1"/>Apply</TabsTrigger>
      </TabsList>
      <TabsContent value="email">
        {/* v2.7.65+E69 · Continue with Google (driver portal).
            Lands on /auth/driver-callback with #session_id=... in the URL fragment. */}
        <GoogleSignIn redirectPath="/auth/driver-callback" testid="driver-google-signin-btn"/>
        <MagicLinkButton portal="driver" defaultEmail={email} testid="driver-magic-link-btn"/>
        <form onSubmit={submitEmail} className="space-y-3 mt-3">
          <div><label className="text-xs text-zinc-400 uppercase tracking-wider">Email</label><Input data-testid="login-email-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required placeholder="Email" className="bg-zinc-950 border-zinc-700 mt-1 h-11"/></div>
          <div><label className="text-xs text-zinc-400 uppercase tracking-wider">Password</label><Input data-testid="login-password-input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required placeholder="Password" className="bg-zinc-950 border-zinc-700 mt-1 h-11"/></div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="driver-remember-me">
            <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="border-zinc-700 data-[state=checked]:bg-amber-400 data-[state=checked]:border-amber-400 data-[state=checked]:text-zinc-950"/>
            <span className="text-[11px] text-zinc-400">Remember me for 30 days</span>
          </label>
          <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "SIGN IN"}</Button>
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => setShowForgot(true)} data-testid="driver-forgot-pw" className="text-[11px] text-zinc-400 hover:text-amber-400 underline-offset-2 hover:underline">
              Forgot password?
            </button>
            <p className="text-[11px] text-zinc-500">Need access? Apply →</p>
          </div>
        </form>
      </TabsContent>
      <TabsContent value="apply"><ApplyPanel/></TabsContent>
      <ForgotPasswordDialog open={showForgot} onOpenChange={setShowForgot} defaultEmail={email}/>
    </Tabs>
  );
}

function RidePanel({ login, register, nav }) {
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);  // v2.7.65+E71
  const [reg, setReg] = useState({ email: "", password: "", first_name: "", last_name: "", phone: "" });
  // v2.7.65+E166 · TCPA-style SMS consent captured at rider signup.
  const [smsConsent, setSmsConsent] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const submitLogin = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await login(email, password, remember); nav("/ride"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };
  const submitRegister = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await register({
        ...reg,
        sms_consent: !!smsConsent,
        sms_consent_ts: smsConsent ? new Date().toISOString() : null,
      });
      toast.success("Welcome! Account created.");
      nav("/ride");
    }
    catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && typeof detail === "object" && detail.code === "duplicate_account") {
        // Tell the rider exactly which fields collided + how to proceed
        toast.error(detail.message, { duration: 7000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Sign-up failed");
      }
    }
    finally { setBusy(false); }
  };

  return (
    <Tabs value={mode} onValueChange={setMode}>
      <TabsList className="grid grid-cols-2 bg-zinc-950 mb-4 h-9">
        <TabsTrigger value="login" data-testid="ride-tab-login" className="text-xs">Sign In</TabsTrigger>
        <TabsTrigger value="signup" data-testid="ride-tab-signup" className="text-xs">Create Account</TabsTrigger>
      </TabsList>

      {/* v2.6.7 batch J — Continue with Google (Emergent-managed flow).
          Lands on /auth/callback with #session_id=... in the URL fragment. */}
      <GoogleSignIn redirectPath="/auth/callback"/>
      <MagicLinkButton portal="customer" defaultEmail={email} testid="ride-magic-link-btn"/>

      <TabsContent value="login">
        <form onSubmit={submitLogin} className="space-y-3 mt-3">
          <Input data-testid="cust-login-email" type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} required className="bg-zinc-950 border-zinc-700 h-11"/>
          <Input data-testid="cust-login-password" type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} required className="bg-zinc-950 border-zinc-700 h-11"/>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="ride-remember-me">
            <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="border-zinc-700 data-[state=checked]:bg-amber-400 data-[state=checked]:border-amber-400 data-[state=checked]:text-zinc-950"/>
            <span className="text-[11px] text-zinc-400">Remember me for 30 days</span>
          </label>
          <Button data-testid="cust-login-btn" type="submit" disabled={busy} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "SIGN IN TO RIDE"}</Button>
          <div className="text-center">
            <button type="button" onClick={() => setShowForgot(true)} data-testid="ride-forgot-pw" className="text-[11px] text-zinc-400 hover:text-amber-400 underline-offset-2 hover:underline">
              Forgot password?
            </button>
          </div>
        </form>
      </TabsContent>
      <TabsContent value="signup">
        <form onSubmit={submitRegister} className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="cust-fname" placeholder="First name" value={reg.first_name} onChange={e=>setReg({...reg, first_name: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-11"/>
            <Input data-testid="cust-lname" placeholder="Last name" value={reg.last_name} onChange={e=>setReg({...reg, last_name: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-11"/>
          </div>
          <Input data-testid="cust-email" type="email" placeholder="Email" value={reg.email} onChange={e=>setReg({...reg, email: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-11"/>
          <Input data-testid="cust-phone" placeholder="Phone" value={reg.phone} onChange={e=>setReg({...reg, phone: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-11"/>
          <SmsConsent
            value={smsConsent}
            onChange={setSmsConsent}
            testIdPrefix="cust-sms-consent"
            className="pt-1"
          />
          <Input data-testid="cust-password" type="password" placeholder="Password" value={reg.password} onChange={e=>setReg({...reg, password: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-11"/>
          <Button data-testid="cust-signup-btn" type="submit" disabled={busy} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "CREATE ACCOUNT"}</Button>
        </form>
      </TabsContent>

      <ForgotPasswordDialog open={showForgot} onOpenChange={setShowForgot} defaultEmail={email}/>
    </Tabs>
  );
}

function ApplyPanel() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", address: "", license_number: "", password: "", pin: "", bg_check_ack: false });
  // v2.7.65+E166 · TCPA-style SMS consent captured at driver apply time.
  const [smsConsent, setSmsConsent] = useState(false);
  // v2.7.65+E69.1 · Google-Apply prefill. When the applicant clicks
  // "Continue with Google" on this panel, AuthApplyCallback verifies
  // the Emergent session and stashes {email, first_name, last_name}
  // in sessionStorage. We hydrate the form here and lock the identity
  // fields (they're now Google-verified — the applicant just fills
  // phone, address, license, password + PIN).
  const [googleVerified, setGoogleVerified] = useState(false);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("google_apply_prefill");
      if (!raw) return;
      const p = JSON.parse(raw);
      if (!p?.email) return;
      setForm((prev) => ({
        ...prev,
        email: p.email,
        first_name: p.first_name || prev.first_name,
        last_name: p.last_name || prev.last_name,
      }));
      setGoogleVerified(true);
      // Consume once — prevents stale prefill on subsequent visits.
      sessionStorage.removeItem("google_apply_prefill");
    } catch (_e) { /* private browsing / bad JSON */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bg_check_ack) return toast.error("You must acknowledge the background check");
    setBusy(true);
    try {
      await api.post("/auth/apply", {
        ...form,
        sms_consent: !!smsConsent,
        sms_consent_ts: smsConsent ? new Date().toISOString() : null,
      });
      setDone(true);
      toast.success("Application submitted");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="text-center py-6 space-y-3">
      <ClipboardCheck className="h-10 w-10 text-green-400 mx-auto"/>
      <div className="text-lg font-bold text-green-400">Application Submitted</div>
      <p className="text-sm text-zinc-400">An administrator will review your application, run a background check, and contact you. You will not be able to sign in until your account is approved.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-2.5">
      {/* v2.7.65+E69.1 · Continue with Google (Apply flow). Lands on
          /auth/apply-callback which stashes the verified profile in
          sessionStorage, then returns to this same panel with the
          identity fields pre-filled. */}
      {!googleVerified && <GoogleSignIn redirectPath="/auth/apply-callback" label="Continue with Google to auto-fill" testid="apply-google-signin-btn"/>}
      {googleVerified && (
        <div
          className="flex items-center gap-2 rounded-md bg-emerald-500/10 border border-emerald-500/40 px-3 py-2"
          data-testid="apply-google-verified-chip"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0" aria-hidden="true">
            <path fill="#EA4335" d="M12 10.2v3.92h5.45c-.24 1.4-1.79 4.13-5.45 4.13-3.28 0-5.96-2.72-5.96-6.07s2.68-6.07 5.96-6.07c1.87 0 3.12.8 3.84 1.49l2.62-2.53C16.95 3.6 14.7 2.6 12 2.6 6.86 2.6 2.7 6.77 2.7 11.9s4.16 9.3 9.3 9.3c5.37 0 8.93-3.78 8.93-9.1 0-.61-.07-1.07-.15-1.53H12z"/>
            <path fill="#34A853" d="M3.91 7.22 6.79 9.34c.78-1.55 2.34-2.63 4.21-2.63 1.16 0 2.21.4 3.04 1.18l2.62-2.53C14.85 4.05 13.5 3.5 12 3.5 8.9 3.5 6.21 5.32 4.83 7.92l-.92-.7z"/>
            <path fill="#FBBC05" d="M12 21.2c2.65 0 4.86-.88 6.48-2.39l-3.09-2.4c-.88.6-2.05.95-3.39.95-2.6 0-4.81-1.75-5.6-4.11l-3.05 2.36C4.79 19.18 8.13 21.2 12 21.2z"/>
            <path fill="#4285F4" d="M21 11.9c0-.61-.07-1.07-.15-1.53H12v3.92h5.45c-.18.95-.94 2.36-2.65 3.18l3.09 2.4C19.5 18.34 21 15.5 21 11.9z"/>
          </svg>
          <span className="text-xs text-emerald-200 font-semibold">Verified with Google · <span className="text-emerald-100">{form.email}</span></span>
        </div>
      )}
      <p className="text-[11px] text-zinc-500 mb-2">Drivers must complete a background check and onboarding before access is granted.</p>
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="apply-fname" placeholder="First name" value={form.first_name} onChange={e=>setForm({...form, first_name: e.target.value})} required readOnly={googleVerified} className={`bg-zinc-950 border-zinc-700 h-10 ${googleVerified ? "opacity-70 cursor-not-allowed" : ""}`}/>
        <Input data-testid="apply-lname" placeholder="Last name" value={form.last_name} onChange={e=>setForm({...form, last_name: e.target.value})} required readOnly={googleVerified} className={`bg-zinc-950 border-zinc-700 h-10 ${googleVerified ? "opacity-70 cursor-not-allowed" : ""}`}/>
      </div>
      <Input data-testid="apply-email" type="email" placeholder="Email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} required readOnly={googleVerified} className={`bg-zinc-950 border-zinc-700 h-10 ${googleVerified ? "opacity-70 cursor-not-allowed" : ""}`}/>
      <Input data-testid="apply-phone" placeholder="Phone" value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-10"/>
      <Input data-testid="apply-address" placeholder="Home address" value={form.address} onChange={e=>setForm({...form, address: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-10"/>
      <Input data-testid="apply-license" placeholder="Driver License #" value={form.license_number} onChange={e=>setForm({...form, license_number: e.target.value})} required className="bg-zinc-950 border-zinc-700 h-10"/>
      <div className="grid grid-cols-2 gap-2">
        <Input
          data-testid="apply-password"
          type="password"
          placeholder={googleVerified ? "Password (optional)" : "Password"}
          value={form.password}
          onChange={e=>setForm({...form, password: e.target.value})}
          required={!googleVerified}
          className="bg-zinc-950 border-zinc-700 h-10"
        />
        <Input
          data-testid="apply-pin"
          placeholder={googleVerified ? "PIN (optional)" : "4-6 digit PIN"}
          maxLength={6}
          value={form.pin}
          onChange={e=>setForm({...form, pin: e.target.value.replace(/\D/g, "")})}
          required={!googleVerified}
          className="bg-zinc-950 border-zinc-700 h-10"
        />
      </div>
      {googleVerified && (
        <p className="text-[10px] text-emerald-300/80 -mt-1" data-testid="apply-google-optional-hint">
          Password + PIN are optional — you can sign in with Google after your application is approved.
        </p>
      )}
      <label className="flex items-start gap-2 p-3 rounded bg-zinc-950 border border-zinc-800 cursor-pointer">
        <Checkbox data-testid="apply-bg-ack" checked={form.bg_check_ack} onCheckedChange={(v)=>setForm({...form, bg_check_ack: !!v})} className="mt-0.5"/>
        <span className="text-xs text-zinc-300">I acknowledge that Buller Taxi Services, LLC will conduct a <span className="text-amber-400 font-bold">background check</span> and full driver onboarding before my account is activated. I authorize the company to verify my identity, license, and driving record.</span>
      </label>
      <SmsConsent
        value={smsConsent}
        onChange={setSmsConsent}
        testIdPrefix="apply-sms-consent"
      />
      <Button data-testid="apply-submit-btn" type="submit" disabled={busy || !form.bg_check_ack} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "SUBMIT APPLICATION"}</Button>
    </form>
  );
}
